// 错误代码消息映射
export const errorCodeMessages: Record<string, string> = {
  // Validation errors (4xxx) - 验证错误
  'E4001': 'Looks like some required fields are missing. Mind checking them?',
  'E4002': 'Could you double-check the information you entered?',
  'E4003': 'Hmm, that username or password doesn\'t look right. Want to try again?',
  'E4004': 'This invitation code might not be valid. Please verify and try again.',
  'E4005': 'The verification code doesn\'t match. Please check and re-enter it.',
  'E4006': 'Don\'t forget to add your product code!',

  // Authentication/Authorization errors (4xxx) - 认证/授权错误
  'E4101': 'You\'ll need to log in first to access this.',
  'E4102': 'We couldn\'t find your account. Would you like to create one?',
  'E4103': 'Just one more step - please verify your email to continue.',
  'E4104': 'Your API key needs verification. Please check and try again.',
  'E4105': 'This feature isn\'t available for your account yet.',
  'E4106': 'We couldn\'t find this user account in our system.',
  'E4107': 'Security check didn\'t go through. Let\'s try that again.',

  // Conflict errors (4xxx) - 冲突错误
  'E4201': 'Good news - you\'re already registered! Try logging in instead.',
  'E4202': 'You\'ve already submitted this application. We\'ll get back to you soon!',

  // Not found errors (4xxx) - 未找到错误
  'E4301': 'We couldn\'t find that application. It might have been moved or deleted.',
  'E4302': 'Your profile information seems to be missing. Let\'s set it up!',
  'E4303': 'This invitation code has expired or doesn\'t exist anymore.',
  'E4304': 'This verification code isn\'t valid. Try requesting a new one?',
  'E4305': 'We don\'t have a record of this email address.',

  // Server errors (5xxx) - 服务器错误
  'E5001': 'We\'re experiencing high traffic right now. Please try again in a moment.',
  'E5002': 'Just loading your data... this will only take a second.',
  'E5003': 'We\'re working on it! Thanks for your patience.',
  'E5004': 'Your email is on its way! It might take a minute to arrive.',
  'E5005': 'Oops! The upload didn\'t work. Mind trying that again?',
  'E5006': 'Your submission didn\'t go through. Mind giving it another try?',
  'E5007': 'Your application is being reviewed. We\'ll update you soon!',
  'E5008': 'We\'re processing your request. Almost done!',
  'E5009': 'The delete didn\'t go through. Want to try once more?',
  'E5010': 'Setting up your account... just a moment!',
  'E5011': 'Sending your invitation now. Hang tight!',
  'E5012': 'Your verification code expired. No worries - just request a fresh one!',
  'E5013': 'There was a hiccup using that invite code. Please try again.',
  'E5014': 'Creating your invitation code... almost ready!',
  'E5015': 'That didn\'t delete properly. Mind trying one more time?',
  'E5016': 'Loading your list... won\'t be long!',
  'E5017': 'Generating your invitation code right now...',

  'E5999': 'That didn\'t quite work. Want to give it another try?'
};

/**
 * 处理API错误响应
 * @param error - 错误对象
 * @returns 包含 error_code 和友好错误消息的对象
 */
export interface ApiError {
  error_code?: string;
  error?: string;
  message?: string;
}

export interface ErrorResult {
  error_code: string;
  message: string;
  originalError?: any;
}

export const handleApiError = (error: any): ErrorResult => {
  // 默认错误信息
  const defaultError: ErrorResult = {
    error_code: 'E5999',
    message: errorCodeMessages['E5999'] || 'An unexpected error occurred. Please try again.',
    originalError: error
  };

  // 如果没有错误对象，返回默认错误
  if (!error) {
    return defaultError;
  }

  // 尝试从不同的可能位置提取 error_code
  const errorCode = error.error_code ||
                    error.code ||
                    error.data?.error_code ||
                    error.response?.data?.error_code;

  // 尝试从不同的可能位置提取错误消息
  const errorMessage = error.error ||
                       error.message ||
                       error.data?.error ||
                       error.response?.data?.error;

  // 如果找到了 error_code，使用对应的友好消息
  if (errorCode && errorCodeMessages[errorCode]) {
    return {
      error_code: errorCode,
      message: errorCodeMessages[errorCode],
      originalError: error
    };
  }

  // 如果有 error_code 但没有对应的友好消息，使用原始错误消息
  if (errorCode) {
    return {
      error_code: errorCode,
      message: errorMessage || defaultError.message,
      originalError: error
    };
  }

  // 如果没有 error_code，但有错误消息，使用默认错误代码
  if (errorMessage) {
    return {
      error_code: defaultError.error_code,
      message: errorMessage,
      originalError: error
    };
  }

  // 返回默认错误
  return defaultError;
};

/**
 * 用于在 catch 块中简化错误处理
 * @param error - 捕获的错误
 * @param callback - 可选的回调函数，用于自定义错误处理
 */
export const catchApiError = (
  error: any,
  callback?: (errorResult: ErrorResult) => void
): ErrorResult => {
  const errorResult = handleApiError(error);

  // 如果提供了回调函数，执行它
  if (callback) {
    callback(errorResult);
  }

  return errorResult;
};
